import { useCallback, useEffect, useRef, useState } from 'react';
import { WELLNESS_CAROUSEL_INTERVAL_MS, type WellnessSlide } from '@lighthouse/shared';
import { WellnessSlidePhoto } from './WellnessSlidePhoto';

interface WellnessCarouselProps {
  slides: WellnessSlide[];
  intervalMs?: number;
  className?: string;
  showCaptions?: boolean;
  /** Pause auto-advance on hover (default off — always auto-scrolls) */
  pauseOnHover?: boolean;
  compact?: boolean;
  autoPlay?: boolean;
}

export function WellnessCarousel({
  slides,
  intervalMs = WELLNESS_CAROUSEL_INTERVAL_MS,
  className = '',
  showCaptions = true,
  pauseOnHover = false,
  compact = false,
  autoPlay = true,
}: WellnessCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);

  const count = slides.length;
  const go = useCallback(
    (dir: 1 | -1) => {
      if (count < 2) return;
      setActive((i) => (i + dir + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (!autoPlay || count < 2 || paused) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % count);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [autoPlay, count, intervalMs, paused]);

  if (count === 0) return null;

  const current = slides[active]!;

  return (
    <div
      className={`wellness-carousel ${compact ? 'wellness-carousel-compact' : ''} ${className}`}
      onMouseEnter={pauseOnHover ? () => setPaused(true) : undefined}
      onMouseLeave={pauseOnHover ? () => setPaused(false) : undefined}
      onTouchStart={(e) => {
        touchStart.current = e.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchStart.current;
        touchStart.current = null;
        if (start === null) return;
        const end = e.changedTouches[0]?.clientX ?? start;
        const delta = end - start;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
      }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Wellness photography"
      aria-live="off"
    >
      <div className="wellness-carousel-track">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`wellness-carousel-slide ${i === active ? 'is-active' : ''}`}
            aria-hidden={i !== active}
          >
            <WellnessSlidePhoto
              slide={slide}
              className="wellness-carousel-zoom"
              priority={i === 0}
            />
          </div>
        ))}
      </div>
      {showCaptions && <p className="wellness-carousel-caption">{current.caption}</p>}
      {count > 1 && (
        <div className="wellness-carousel-dots" role="tablist" aria-label="Slide navigation">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={slide.caption}
              className={`wellness-carousel-dot ${i === active ? 'is-active' : ''}`}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
